import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import Toast from 'components/toast';
import GlobalLoader from 'components/global-loader';
import buyApartmentFlowRouter from 'routes/buy-apartment';
import sellApartmentFlowRouter from 'routes/sell-apartment';
import notificationFlowRouter from 'routes/notification';
import AuthBottomSheet from 'components/login-bottom-sheet';

import TGApi from 'lib/webapp-api';
import { authorize } from 'api';
import useBoundStore from 'store';

import './index.scss';
import temporaryRouter from './routes/temporaryPaths';
import commonRouter from 'routes/common';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FallbackComponent } from 'components/fallback';
import { useAnalytics } from 'hooks/useAnalytics';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    element: (
      <Sentry.ErrorBoundary fallback={FallbackComponent}>
        <Outlet />
      </Sentry.ErrorBoundary>
    ),
    children: [
      ...commonRouter,
      ...buyApartmentFlowRouter,
      ...sellApartmentFlowRouter,
      ...notificationFlowRouter,
      ...temporaryRouter,
    ],
  },
]);

function App() {
  const [loaded, setLoaded] = useState(false);
  const globalLoader = useBoundStore((s) => s.globalLoader);
  const toggleGlobalLoader = useBoundStore((s) => s.toggleGlobalLoader);

  useAnalytics();

  useEffect(() => {
    const auth = async () => {
      const initData = TGApi.getInitData();

      if (initData) {
        toggleGlobalLoader();
        await authorize(TGApi.context, initData);
        toggleGlobalLoader();
      }

      setLoaded(true);
    };

    auth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const maxStartParam = window.WebApp?.initDataUnsafe?.start_param;
    if (typeof maxStartParam !== 'string' || !maxStartParam) return;

    function decodePayload(payload: string): string {
      let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

      const pad = base64.length % 4;
      if (pad === 2) base64 += '==';
      else if (pad === 3) base64 += '=';
      else if (pad === 1) throw new Error('Invalid payload: length % 4 === 1');

      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

      return new TextDecoder().decode(bytes);
    }

    router.navigate(decodePayload(maxStartParam), { replace: true });
  }, []);

  const [initHeight, setInitHeight] = useState<number>(0);

  const setKeyBoardHeight = useBoundStore((s) => s.setKeyBoardHeight);

  const handleKeyboardHeight = useCallback(() => {
    const currentHeight = window.visualViewport?.height;
    if (initHeight && currentHeight) {
      setKeyBoardHeight(initHeight - currentHeight);
    }
  }, [setKeyBoardHeight, initHeight]);

  useEffect(() => {
    const initialHeight = window.visualViewport?.height;
    initialHeight && setInitHeight(initialHeight);
  }, []);

  useEffect(() => {
    window.visualViewport?.addEventListener('resize', handleKeyboardHeight);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleKeyboardHeight);
    };
  }, [handleKeyboardHeight]);

  return (
    <>
      {loaded && (
        <>
          <RouterProvider router={router} />
          <AuthBottomSheet />
        </>
      )}

      <Toast />
      <ToastContainer position='bottom-right' autoClose={false} />
      {globalLoader && <GlobalLoader />}
    </>
  );
}

export default App;
